import Image from 'next/image';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import Head from 'next/head';
import { useDispatch } from 'react-redux';

import LocationInput from 'components/LocationInput';
import Dialog from 'components/Dialog';
import MakeModelMenu from 'components/Shared/MakeModelMenu';
import OnboardingModal from 'components/Onboarding/Modal';

import { UsedCarsWelcomePageViewEvent } from 'lib/Tracking/PaidLandingPage';
import { useBodyTypesFilter, useCategoryFilter, useMakeModelFilter } from 'lib/hooks/useSrpFilters';
import useEvents from 'lib/hooks/useEvents';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import useAdSetup from 'lib/hooks/useAdSetup';
import { PageType, AgeType, Zone } from 'lib/CarGurusAds/TaxonomySchema';
import useGeoLocation from 'lib/hooks/useGeoLocation';
import { trackPageView } from 'lib/FacebookPixel';
import { AppDispatch } from 'redux-implementations/makeStore';
import { loadMakesAndModels } from 'redux-implementations/makesAndModels';

import socialProof from 'public/static/images/used_cars_welcome/social proof.png';
import searchIcon from 'public/static/images/paid_landing/search-icon.svg';

import Make from 'types/Make';
import Model from 'types/Model';
import categories from './utils';
import styles from './styles';

export default function UsedCarsWelcome(): ReactElement {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { value: bodyTypeValue, setValue: setBodyTypeValue } = useBodyTypesFilter();
  const { setValue: setCategoryValue } = useCategoryFilter();
  const logger = useEvents();
  const { setValue } = useMakeModelFilter();
  const dispatch = useDispatch<AppDispatch>();
  useGeoLocation(false);

  useEffectOnce(() => {
    dispatch<any>(loadMakesAndModels());
  });

  useEffectOnce(() => {
    logger.log(
      UsedCarsWelcomePageViewEvent({
        sourceUrl: window?.location?.href,
      })
    );
    trackPageView();
  });

  function onSelectCategory(value: string): void {
    if (value === 'Electric' || value === 'Hybrid' || value === 'Sport') {
      setCategoryValue(value);
    } else {
      setBodyTypeValue({ ...bodyTypeValue, [value]: true });
    }
    setIsModalOpen(true);
  }

  function handleMakeModelSearch(make?: Make, model?: Model): void {
    setValue({
      make: make?.name,
      model: model?.name,
    });
    setIsModalOpen(true);
  }

  const carGurusAdUnit = new CarGurusAdUnit({ zone: Zone.Research });
  useAdSetup(carGurusAdUnit.toString(), {
    make: null,
    model: null,
    year: null,
    pageType: PageType.HomePageUsedCarLanding,
    ageType: AgeType.None,
    cpo: false,
    inLine: 1,
    body_style: null,
    category: null,
    app: 'web',
    sellerId: null,
  });

  return (
    <>
      <Head>
        <meta content="noindex, follow" name="robots" />

        <meta
          content="//production-assets2.autolist.com/assets/ms-icon-144x144-497ab60279119e4c12ce548da7bc2c53a7d225df10b37d221aa1e093639296ca.png"
          name="msapplication-TileImage"
        />
        <meta content="#ffffff" name="theme-color" />
        <meta content="#ffffff" name="msapplication-TileColor" />

        <meta content="1200" property="og:image:width" />
        <meta content="630" property="og:image:height" />

        <meta content="summary_large_image" name="twitter:card" />
        <meta content="@autolistcom" name="twitter:site" />
        <meta
          content="https://production-assets0.autolist.com/assets/social_media/og_default_placeholder-2d205783892f9dd28f21a2c45a127a0870a03247dbda1b74d37e615b448b0306.png"
          name="twitter:image"
        />
        <meta
          content="https://production-assets0.autolist.com/assets/social_media/og_default_placeholder-2d205783892f9dd28f21a2c45a127a0870a03247dbda1b74d37e615b448b0306.png"
          property="og:image"
        ></meta>
      </Head>
      <div>
        <div className="navbar">
          <div className="onboarding-autolist-logo" />
        </div>
        <Dialog
          button={({ toggle }): JSX.Element => (
            <>
              <div className="content-container">
                <div className="search-header">
                  <div className="header-content">
                    <div className="header-title">{t('paidLanding.welcomeTitle')}</div>
                    <div className="searching-section">
                      <span>{t('paidLanding.searchingIn')}</span>
                      <div className="location-container">
                        <LocationInput className="paid-location-input" />
                      </div>
                    </div>
                    <div className="search-bar">
                      <div className="search-icon">
                        <Image src={searchIcon} width={21} height={22} alt="search-icon" />
                      </div>
                      <div className="search-input" onClick={toggle}>
                        Search make & model
                      </div>

                      <div className="search-button" onClick={toggle}>
                        {t('paidLanding.search')}
                      </div>
                    </div>
                  </div>
                  <div className="social-proof">
                    <Image src={socialProof} width={725} height={57} alt="social proof" />
                  </div>
                </div>
                <div className="category-header">
                  <div className="line" />
                  {t('paidLanding.browseBy')}
                  <div className="line" />
                </div>
                <div className="vehicle-category-container">
                  {categories.map(({ value, label, image }) => (
                    <div
                      className="vehicle-category"
                      key={label}
                      onClick={(): void => onSelectCategory(value)}
                      data-testid="category-button"
                    >
                      <div>
                        <img src={image} alt={label} className="vehicle-image" />
                      </div>
                      <div className="vehicle-label">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          content={({ hide }): JSX.Element => (
            <MakeModelMenu
              allMakesAndModelsOption={true}
              onSelect={(make?: Make, model?: Model): void => {
                handleMakeModelSearch(make, model);
                hide();
              }}
              mobileHeight="full"
            />
          )}
        />
        <OnboardingModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isPending={false}
          isUsedCarsWelcome
        />
        <style>{styles}</style>
      </div>
    </>
  );
}
